import React from "react";
import { isEmpty } from "lodash";
import classNames from "classnames";
import {
  Async as AsyncReactSelect,
  AsyncCreatable as AsyncCreatableReactSelect,
  Creatable as CreatableReactSelect,
  LoadOptionsAsyncHandler,
  ReactAsyncSelectProps,
  ReactCreatableSelectProps
} from "react-select";
import { withStyles } from "@material-ui/core";
// undocumented feature:
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { styles as muiInputStyles } from "@material-ui/core/Input/Input";
import ReactSelect from "./ReactSelect";
import ArrowDropDownIcon from "react-icons/lib/md/arrow-drop-down";
import ArrowDropUpIcon from "react-icons/lib/md/arrow-drop-up";
import ClearIcon from "react-icons/lib/md/clear";

import { ThemeType, Classes } from "@theme";
import locale from "@bonliva-crm/locale";

import { DefaultMenuRenderer } from "./DefaultMenuRenderer";
import GroupedMenuRenderer from "./GroupedMenuRenderer";
import { NestedMenuRenderer } from "./NestedMenuRenderer/NestedMenuRenderer";
import { EmptyMenuRenderer } from "./EmptyMenuRenderer";
import { SelectValueChip } from "./SelectValueChip";
import { SelectValuePlain } from "./SelectValuePlain";
import { SelectOptionMui as SelectOptionPlain } from "./SelectOptionPlain";
import { SelectOptionMui as SelectOptionCheckbox } from "./SelectOptionCheckbox";
import { SelectOptionCheckboxTwoLine } from "./SelectOptionCheckboxTwoLine";
import {
  OptionType,
  Option,
  MenuRendererProps,
  MultiType,
  sortOptionsToSortBy
} from "./utils";
import Body from "../typography/Body";
import { Small } from "../typography/Small";
import { NestedGroupedMenuRenderer } from "@common/Select/NestedGroupedMenuRenderer";

type OmitReactSelectProps = "loadOptions" | "options" | "onChange" | "value";

export type ReactSelectMuiProps = Omit<
  Partial<ReactAsyncSelectProps<Option>> & Partial<ReactCreatableSelectProps<Option>>,
  OmitReactSelectProps
> & {
  autoOpen?: boolean;
  creatable?: boolean;
  className?: string;
  disableUnderline?: boolean;
  error?: boolean;
  expanded?: boolean;
  initialOption?: Option;
  filterOptionsWhenTyping?: boolean;
  fullWidth?: boolean;
  hasMuiLabel?: boolean;
  hideGroupCheckbox?: boolean;
  hideSameNameChildInGroups?: boolean;
  groupBy?: string; // | ((options: Option[]) => Dictionary<Option[]>);
  groupByField?: string;
  loadOptions?: LoadOptionsAsyncHandler;
  multiType?: MultiType;
  nested?: boolean; // groupBy will be ignored
  options?: string[] | Option[];
  optionType?: OptionType;
  onChange?: ((value: Option) => any) | ((value: Option[]) => any);
  onClose?: () => void;

  renderDescriptionInValues?: boolean;
  renderMenu?: (menu: React.ReactElement<any>) => React.ReactElement<any>;
  wrapMenu?: (menu: React.ReactElement<any>) => React.ReactElement<any>;
  sortGroups?: boolean;
  sortValues?: boolean;
  /**
   * if set to true will sort by label as, otherwise a list of lodash iteratees can be provided
   * set false to avoid sorting (default)
   */
  sortOptions?: boolean | ArraySortIteratee<Option> | Array<ArraySortIteratee<Option>>;
  value?: string | Option | string[] | Option[];
  showSearchInput?: boolean;
  skipChipDeleteIcon?: boolean;
  disablePortal?: boolean;
};

type Props = ReactSelectMuiProps & {
  classes: Classes<typeof styles>;
};

type State = {
  openedGroups: string[];
};

export class ReactSelectMui extends React.Component<Props, State> {
  static defaultProps: Partial<Props> = {
    groupByField: "value",
    sortGroups: true,
    multiType: "plain" as MultiType,
    renderDescriptionInValues: true
  };

  selectRef = React.createRef<any>();

  constructor(props) {
    super(props);
    this.state = { openedGroups: [] };
  }

  filterOptionsGrouped = (options: Option[], filter: string) => {
    const { filterOptionsWhenTyping } = this.props;
    const re = new RegExp(filter, "i");

    if (!filterOptionsWhenTyping) {
      return options;
    }

    const result = options.filter((o) => {
      return (
        re.test(o.label as string) ||
        // eslint-disable-next-line no-prototype-builtins
        (o.hasOwnProperty("children") &&
          o.children.length &&
          o.children.some(
            (childrenOption) =>
              re.test(childrenOption.label) || re.test(childrenOption.descrption)
          )) ||
        (typeof o.description === "string" && re.test(o.description))
      );
    });

    return result;
  };

  componentDidMount() {
    if (this.props.autoOpen) {
      if (this.props.loadOptions) {
        this.selectRef?.current.select.openMenu();
      } else {
        this.selectRef?.current.openMenu();
      }
    }
  }

  render() {
    const {
      className: classNameProp,
      classes,
      clearable = true,
      creatable,
      disabled,
      error,
      expanded,
      filterOptionsWhenTyping,
      fullWidth,
      groupBy: groupByProp,
      hasMuiLabel,
      multiType,
      optionType,
      optionRenderer,
      placeholder: placeholderProp = "",
      renderDescriptionInValues,
      sortOptions,
      sortValues,
      value: valueProp,
      disablePortal = true,
      ...rest
    } = this.props;

    // any prevents some weird ts errors, this is probably to complicated
    // for typescript to handle
    const Component = (
      this.props.loadOptions
        ? creatable
          ? AsyncCreatableReactSelect
          : AsyncReactSelect
        : creatable
        ? CreatableReactSelect
        : ReactSelect
    ) as any;

    // get root class name
    const className = classNames("react-select-mui", classes.root, classes.formControl, {
      [classes.disabled]: disabled && !error,
      [classes.error]: error && !disabled,
      [classes.errorDisabled]: error && disabled,
      [classes.fullWidth]: fullWidth,
      [classes.multiline]: false,
      [classes.underline]: !this.props.disableUnderline
    });

    // get react-select class name
    const inputClassName = classNames(
      classes.input,
      {
        [classes.inputDisabled]: disabled,
        [classes.inputType]: false,
        [classes.inputTypeSearch]: true,
        [classes.inputMultiline]: false,
        [classes.inputMarginDense]: false
      }
      // inputPropsClassName,
    );

    // get placeholder value
    const placeholder = hasMuiLabel ? (error ? "" : placeholderProp) : placeholderProp;

    let value = valueProp;

    if (this.props.multi && sortValues) {
      value = this.props.loadOptions
        ? (valueProp as Option[]).sortBy((x) => x.label as string)
        : (valueProp as string[]).sortBy((x) => x);
    }

    const filterOptions = sortOptions
      ? (options: Option[], filter: string) => {
          const re = new RegExp(filter, "ig");
          return (
            filterOptionsWhenTyping
              ? options.filter(
                  (o) =>
                    (typeof o.label === "string" && o.label.match(re)) ||
                    (typeof o.description === "string" && o.description.match(re))
                )
              : options
          ).sortBy(...sortOptionsToSortBy(sortOptions));
        }
      : undefined;

    let cutRenderArr: string[] = value as string[];
    if (this.props.multi && !this.props.loadOptions) {
      if (value?.length > 10) {
        cutRenderArr = (value as string[]).slice(0, 10);
      }
    }

    return (
      <div className={className} data-test="ReactSelectMui">
        <Component
          ref={this.selectRef}
          arrowRenderer={(arrowProps: { isOpen: boolean }) =>
            arrowProps.isOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />
          }
          children={(props) => <ReactSelect {...props} />}
          className={inputClassName}
          clearable={clearable}
          clearRenderer={() => (clearable ? <ClearIcon /> : null)}
          disabled={disabled}
          filterOptions={this.props.groupBy ? this.filterOptionsGrouped : filterOptions}
          menuRenderer={(rendererProps: MenuRendererProps) => {
            let menu;
            if (isEmpty(rendererProps.options)) {
              menu = (
                <EmptyMenuRenderer
                  text={locale.NothingFound}
                  instancePrefix={rendererProps.instancePrefix}
                  onClose={() => this.selectRef.current.closeMenu()}
                />
              );
            } else if (this.props.nested && groupByProp) {
              menu = (
                <NestedGroupedMenuRenderer
                  showSearchInput={this.props.showSearchInput}
                  {...rendererProps}
                  expanded={expanded}
                  disablePortal={disablePortal}
                  isOpen={
                    // this.selectRef.current ? this.selectRef.current.state.isOpen : false
                    this.selectRef.current?.state?.isOpen || false
                  }
                  onClose={() => this.selectRef.current.closeMenu()}
                />
              );
            } else if (this.props.nested) {
              menu = (
                <NestedMenuRenderer
                  {...rendererProps}
                  isOpen={
                    // this.selectRef.current ? this.selectRef.current.state.isOpen : false
                    this.selectRef.current?.state?.isOpen || false
                  }
                  onClose={() => this.selectRef.current.closeMenu()}
                />
              );
            } else if (groupByProp) {
              menu = (
                <GroupedMenuRenderer
                  {...rendererProps}
                  hideGroupCheckbox={this.props.hideGroupCheckbox}
                  renderMenu={this.props.renderMenu}
                  onClose={() => this.selectRef.current.closeMenu()}
                  openedGroups={this.state.openedGroups}
                  selectProps={this.props}
                  closeGroup={(g) =>
                    this.setState({
                      openedGroups: this.state.openedGroups.filter((item) => item !== g)
                    })
                  }
                  openGroup={(g) =>
                    this.setState({ openedGroups: [...this.state.openedGroups, g] })
                  }
                  sortGroups={Boolean(this.props.sortGroups)}
                />
              );
            } else {
              menu = (
                <DefaultMenuRenderer
                  {...rendererProps}
                  renderMenu={this.props.renderMenu}
                  onClose={() => {
                    if (this.props.loadOptions) {
                      // Select is hidden inside Async
                      return this.selectRef.current.select.closeMenu();
                    } else {
                      return this.selectRef.current.closeMenu();
                    }
                  }}
                />
              );
            }

            return this.props.wrapMenu ? this.props.wrapMenu(menu) : menu;
          }}
          noResultsText={<Body>{locale.NothingFound}</Body>}
          onInputKeyDown={(e: React.SyntheticEvent<any>) => {
            e.stopPropagation();
          }}
          optionComponent={
            optionType === "checkbox"
              ? SelectOptionCheckbox
              : optionType === "checkboxTwoLine"
              ? SelectOptionCheckboxTwoLine
              : SelectOptionPlain
          }
          // optionsRendered={console.info}
          optionRenderer={optionRenderer}
          searchable
          placeholder={placeholder}
          value={value}
          valueComponent={(valueProps) => {
            const {
              value: itemValue,
              children,
              onRemove: onRemoveProp,
              ...restValueProps
            } = valueProps;

            let cut = false;
            if (this.props.multi && !this.props.loadOptions) {
              cut = !cutRenderArr.find((x) => x === itemValue.value);
              if (cut) {
                return null;
              }
            }

            const ChildComponent =
              multiType === "chips" ? SelectValueChip : SelectValuePlain;

            return (
              <ChildComponent
                skipChipDeleteIcon={this.props.skipChipDeleteIcon}
                label={children}
                onRemove={() => onRemoveProp(itemValue)}
                value={itemValue}
                {...restValueProps}
              />
            );
          }}
          valueRenderer={(option: Option) => {
            let moreLabel = "";
            if (cutRenderArr && value.length > 10 && option.label === cutRenderArr[9]) {
              moreLabel = ".... + " + (value.length - 10) + " " + locale.More;
            }

            return option.description && renderDescriptionInValues ? (
              <>
                {option.label}
                <Small className={classes.description} display={"block"}>
                  {option.description}
                </Small>
              </>
            ) : (
              option.label + moreLabel
            );
          }}
          {...rest}
        />
      </div>
    );
  }
}

const styles = (theme: ThemeType) => {
  const inputStyles = muiInputStyles(theme);

  return {
    ...inputStyles,

    input: {
      ...inputStyles.input,
      padding: "6px 0 5px",
      "& .Select-option span:last-child": {
        width: "100%"
      },
      fontFamily: theme.typography.fontFamily
    },

    inputTypeSearch: {
      appearance: "none"
    },

    errorDisabled: {
      color: theme.palette.text.disabled,

      "&:before": {
        background: "transparent",
        backgroundImage: `linear-gradient(to right, ${theme.palette.error.main} 33%, transparent 0%)`,
        backgroundPosition: "left bottom",
        backgroundRepeat: "repeat-x",
        backgroundSize: "5px 1px",
        transform: "scaleX(1)",
        borderColor: ["#00000000", "!important"]
      }
    },

    description: {
      // display: "inline",
      // marginLeft: theme.spacing(1 / 2)
    }
  };
};

export default withStyles(styles as any)(ReactSelectMui);
